import { ActuatorControlMapper } from "./ActuatorControlMapper";
import { setActuatorControlNamingMode } from "./namingMode";
import type { Actuator, MappedJoint, MapperOptions } from "./types";

let globalMapper: ActuatorControlMapper | null = null;

/**
 * Gets the global actuator control mapper instance
 * @returns The global mapper instance
 */
export function getGlobalActuatorMapper(): ActuatorControlMapper {
  if (!globalMapper) {
    globalMapper = new ActuatorControlMapper();
  }
  return globalMapper;
}

/**
 * Updates the global mapper with current robot state
 * @param actuators Array of actuators from robot state
 * @param joints Array of joints from robot state
 * @param forceRebuild Force rebuilding even if cache is valid
 * @returns Object with mapping results and errors/warnings
 */
export function updateGlobalActuatorMapping(
  actuators: Actuator[],
  joints: MappedJoint[] = [],
  options: MapperOptions = {},
  forceRebuild: boolean = false
): {
  success: boolean;
  errors: string[];
  warnings: string[];
  mappedCount: number;
  fromCache: boolean;
} {
  const mapper = getGlobalActuatorMapper();
  const wasUsingCache = mapper.isUsingCache();

  mapper.buildMapping(actuators, joints, options, forceRebuild);

  const errors = mapper.getErrors();
  const warnings = mapper.getWarnings();
  const mappedCount = mapper.getControlKeys().length;
  const fromCache = !forceRebuild && wasUsingCache && mapper.isUsingCache();

  return {
    success: !mapper.hasErrors(),
    errors,
    warnings,
    mappedCount,
    fromCache,
  };
}

/**
 * Gets control key for an actuator name using global mapper
 * @param actuatorName The actuator name
 * @returns The control key
 */
export function getActuatorControlKey(actuatorName: string): string {
  return getGlobalActuatorMapper().getControlKey(actuatorName);
}

/**
 * Creates control payload using global mapper
 * @param actuatorValues Object mapping actuator names to values
 * @param actuators Array of actuators for fallback
 * @returns Control payload
 */
export function createActuatorControlPayload(
  actuatorValues: Record<string, number>,
  actuators: Actuator[] = []
): Record<string, number> {
  return getGlobalActuatorMapper().createControlPayload(actuatorValues, actuators);
}

/**
 * Creates a runtime control payload from user-facing position targets.
 *
 * Position sliders and joint-label drags store targets in joint space; this maps
 * affine general servos to the raw MuJoCo ctrl values needed to represent those targets.
 */
export function createPositionActuatorControlPayload(
  actuatorPositionTargets: Record<string, number>,
  actuators: Actuator[] = []
): Record<string, number> {
  return getGlobalActuatorMapper().createPositionControlPayload(actuatorPositionTargets, actuators);
}

/**
 * Gets errors from the global mapper
 * @returns Array of error messages
 */
export function getGlobalActuatorMappingErrors(): string[] {
  return getGlobalActuatorMapper().getErrors();
}

/**
 * Gets warnings from the global mapper
 * @returns Array of warning messages
 */
export function getGlobalActuatorMappingWarnings(): string[] {
  return getGlobalActuatorMapper().getWarnings();
}

/**
 * Checks if the global mapper has errors
 * @returns True if there are mapping errors
 */
export function hasGlobalActuatorMappingErrors(): boolean {
  return getGlobalActuatorMapper().hasErrors();
}

/**
 * Checks if the global mapper has warnings
 * @returns True if there are mapping warnings
 */
export function hasGlobalActuatorMappingWarnings(): boolean {
  return getGlobalActuatorMapper().hasWarnings();
}

/**
 * Forces a rebuild of the global mapper cache
 */
export function invalidateGlobalActuatorMappingCache(): void {
  getGlobalActuatorMapper().invalidateCache();
}

/**
 * Checks if the global mapper is using cached data
 * @returns True if current mapping came from cache
 */
export function isGlobalActuatorMappingUsingCache(): boolean {
  return getGlobalActuatorMapper().isUsingCache();
}

export function setActiveActuatorControlNamingMode(mode: "stableUuid" | "sceneName"): void {
  setActuatorControlNamingMode(mode);
  if (globalMapper) {
    globalMapper.invalidateCache();
  }
}
